/**
 * Ziggy — the EduViaOS mascot & AI-assistant persona.
 *
 * Ziggy is a friendly bear with a fixed, recognisable identity (signature
 * daisy hoodie) whose *accents* re-tint to the live tenant palette (handled
 * in the <Ziggy> component via CSS `var(--color-primary)`). Two looks ship —
 * a warm **pink** Ziggy and a cool **blue** Ziggy — and every learner picks
 * the one they want (persisted; see `ziggyContext`). Art is age-aware: a
 * Class-5 cub and a Class-8 teen do not see the same avatar, and
 * study-centre adults / all guardians see the grown variant.
 *
 * Art assets live at `/art/ziggy/<variant>/ziggy-<age>-<pose>.png` (true-alpha
 * cutouts). Every variant × age × pose cell is authored (48 total); `ziggySrc`
 * still degrades defensively to `happy` for any unknown pose.
 */

export type ZiggyVariant = "pink" | "blue";

export type ZiggyAge = "cub" | "teen" | "grown";

/**
 * The eight canonical expressions (Expressions + Activity rows of the Ziggy
 * brand sheet). Every one is authored for both variants and all three ages,
 * and every one is used somewhere in the portal.
 */
export type ZiggyPose =
  | "happy"
  | "thinking"
  | "excited"
  | "curious"
  | "surprised"
  | "cheerful"
  | "reading"
  | "standing";

export const ZIGGY_VARIANTS: readonly ZiggyVariant[] = ["pink", "blue"];

export const ZIGGY_POSES: readonly ZiggyPose[] = [
  "happy",
  "thinking",
  "excited",
  "curious",
  "surprised",
  "cheerful",
  "reading",
  "standing",
];

export const DEFAULT_ZIGGY_VARIANT: ZiggyVariant = "pink";

/**
 * Presentation metadata for the two looks (chooser + settings). Labels are
 * product names and stay as-is in every locale; the descriptive blurb lives in
 * the portal shell locale table so it can be translated.
 */
export const ZIGGY_VARIANT_META: Record<ZiggyVariant, { label: string; swatch: string }> = {
  pink: { label: "Daisy Pink", swatch: "oklch(0.66 0.2 340)" },
  blue: { label: "Sky Blue", swatch: "oklch(0.64 0.15 235)" },
};

const isPose = (pose: string): pose is ZiggyPose =>
  (ZIGGY_POSES as readonly string[]).includes(pose);

export const isZiggyVariant = (value: string | null | undefined): value is ZiggyVariant =>
  !!value && (ZIGGY_VARIANTS as readonly string[]).includes(value);

/**
 * Resolve the art path for a look × age × pose. All cells are authored, so
 * this only guards against an unknown pose (→ `happy`).
 */
export function ziggySrc(variant: ZiggyVariant, age: ZiggyAge, pose: ZiggyPose): string {
  const p: ZiggyPose = isPose(pose) ? pose : "happy";
  return `/art/ziggy/${variant}/ziggy-${age}-${p}.png`;
}

/**
 * Derive Ziggy's age band from a learner's class/grade label.
 * "Class 5" → cub, "Class 8"/"Grade 11" → teen, anything without a
 * primary-school number (adult learners, "Foundation", etc.) → grown.
 */
export function ageBandFromClass(classLabel: string | null | undefined): ZiggyAge {
  if (!classLabel) return "teen";
  const m = classLabel.match(/\d+/);
  if (!m) return "grown";
  const grade = Number(m[0]);
  if (grade <= 5) return "cub";
  if (grade <= 12) return "teen";
  return "grown";
}

/** A friendly one-liner Ziggy uses when introducing himself. */
export const ZIGGY_TAGLINE = "Your study buddy";
